import TextField from '@mui/material/TextField'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import Typography from '@mui/material/Typography'
import { Chip, useTheme } from '@mui/material'
import { useActiveOrganization } from 'hooks/use-organization'
import { useSearchTags } from 'hooks/use-tags'
import { COLORS } from 'app-constants'

const filter = createFilterOptions<string>()

const TagsAutocomplete = ({
  initialValue,
  onValueChanged,
}: {
  initialValue: string[]
  onValueChanged: (value: string[]) => void
}) => {
  const theme = useTheme()
  const [activeOrganization] = useActiveOrganization()

  const { setQuery, pendingInputValue, options, setOptions, isLoading, debounceQueryUpdate } = useSearchTags()

  return (
    <Autocomplete
      fullWidth
      loading={isLoading}
      multiple
      filterSelectedOptions
      getOptionLabel={(option) => option}
      isOptionEqualToValue={(option, value) => option === value}
      options={options}
      autoComplete
      autoHighlight
      popupIcon={''}
      disabled={false}
      disableClearable
      value={initialValue}
      onChange={(event, newValue) => {
        setOptions([...newValue, ...options])
        onValueChanged(newValue.map((a) => a))
      }}
      onInputChange={(event, newInputValue) => {
        setQuery(newInputValue)

        if (!activeOrganization) {
          return
        }

        pendingInputValue.current = newInputValue
        debounceQueryUpdate(newInputValue, activeOrganization)
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params)

        const { inputValue } = params
        // Suggest the creation of a new value
        const isExisting = options.some((option) => inputValue === option)
        if (inputValue !== '' && !isExisting) {
          filtered.push(inputValue)
        }

        return filtered
      }}
      renderInput={(params) => <TextField {...params} placeholder="Add tag" />}
      renderOption={(props, option) => {
        const isExisting = options.some((o) => option === o)

        const color = () => {
          if (theme.palette.mode === 'light') {
            return isExisting ? COLORS.GRAY_0 : COLORS.JELLED_PRIMARY_BLUE
          } else {
            return isExisting ? COLORS.PURE_WHITE : COLORS.JELLED_PRIMARY_BLUE
          }
        }

        return (
          <li {...props}>
            <Typography variant={isExisting ? 'body1' : 'h5'} color={color()}>
              {!isExisting ? `Create "${option}"` : `${option}`}
            </Typography>
          </li>
        )
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            label={option}
            {...getTagProps({ index })}
            color="primary"
            sx={{
              background: COLORS.PURE_BLACK,
            }}
          />
        ))
      }
      sx={{
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${COLORS.JELLED_PRIMARY_BLUE}`,
        },
        '&:hover:not(.Mui-focused) .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${COLORS.GRAY_5}`,
        },
      }}
    />
  )
}

export default TagsAutocomplete
